import React, { Component, Fragment, useEffect, useState, useRef } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import _ from "lodash";
import Swal from "sweetalert2";
import {
  getSpecialistMeetingMaster,
  postMeetingRequest,
  getSpecialistMeetingRequestCoordinators,
} from "../../../Services/SpecialistMeetingServices";
import { utils } from "../../../helpers/utils";

export type RSASpecialistMeeingFormValues = {
  [key: string]: any;
};

export type RSASpecialistMeeingFormRef = {
  [key: string]: React.RefObject<HTMLElement | null>;
};

export type RSASpecialistMeeingFormErrors = {
  [key: string]: string;
};

export type RSASpecialistMeeingFormTouched = {
  [key: string]: boolean;
};

export type ValidtionFunctions = {
  [key: string]: Function;
};

export type RSASpecialistMeeingFormMasterData =
  RSASpecialistMeeingFormMasterDataRow[];

export type RSASpecialistMeeingFormMasterDataRow = {
  employmentHelpTopicId: number;
  displayName: string | null;
  askMoreInfo: boolean | null;
  moreInfoText: string | null;
};

export type MasterDataIdValueOption = {
    id: number,
    value: string,
}

export type DropdownOption = {
    text: string,
    value: string | number,
}

export function useRSASpecialistMeetingForm() {
  const history = useHistory();

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      //get master data
      getSpecialistMeetingMaster(false).then((response) => {
        if (response && response.status === 200 && response.data) {
          let newData: RSASpecialistMeeingFormMasterData =
            response.data as RSASpecialistMeeingFormMasterData;
          let otherAssistanceOptionIndex: number = newData.findIndex((item) => {
            if (item.displayName?.toLowerCase().includes("assistance")) {
              return true;
            } else {
              return false;
            }
          });
          if (otherAssistanceOptionIndex !== -1) {
            let otherAssistanceOption = newData[otherAssistanceOptionIndex];
            newData.splice(otherAssistanceOptionIndex, 1);
            newData.push(otherAssistanceOption);
          }
          setMasterData(newData);
        } else {
          throw new Error(
            "Unable to fetch Employer Spotlight Presentation data!"
          );
        }
      });

      getSpecialistMeetingRequestCoordinators(false).then((response) => {
        if (response && response.status === 200 && response.data) {
          let newData: MasterDataIdValueOption[] =
            response.data as MasterDataIdValueOption[];
            setCoordinatorMaster(newData.map((item)=>{
                return {
                    text: item.value,
                    value: item.id
                }
            }));
        } else {
          throw new Error(
            "Unable to fetch available employment coordinators' data!"
          );
        }
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error While Processing",
        text: `Unable to get necessory data for the form.`,
        showConfirmButton: true,
        confirmButtonText: "OK",
        confirmButtonColor: "#1678c2",
      }).then(() => {});
    }
  }

  const defaultValues: RSASpecialistMeeingFormValues = {
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    confirmEmail: "",
    coordinatorId: null,
    helpTopicIds: [],
    otherAssistance: "",
    preferedDateTime: "",
    comments: "",
  };
  const validations = {
    firstName: async function (values: RSASpecialistMeeingFormValues) {
      // Do validation here
      // two promises, one for local, one for async, usually serverside.
      let error: string | null = null;
      let promises = [
        new Promise((resolve, reject) => {
          // local validation logic here.
          if (!values.firstName) {
            error = "Please enter first name";
          }
          if (error != null) {
            reject(error);
          } else {
            resolve(null);
          }
        }),
        new Promise((resolve, reject) => {
          // do server validation logic here.
          resolve(null);
        }),
      ];
      let result = await Promise.all(promises);
      return result;
    },
    lastName: async function (values: RSASpecialistMeeingFormValues) {
      // Do validation here
      // two promises, one for local, one for async, usually serverside.
      let error: string | null = null;
      let promises = [
        new Promise((resolve, reject) => {
          // local validation logic here.
          if (!values.lastName) {
            error = "Please enter last name";
          }
          if (error != null) {
            reject(error);
          } else {
            resolve(null);
          }
        }),
        new Promise((resolve, reject) => {
          // do server validation logic here.
          resolve(null);
        }),
      ];
      let result = await Promise.all(promises);
      return result;
    },
    phoneNumber: async function (values: RSASpecialistMeeingFormValues) {
      // Do validation here
      // two promises, one for local, one for async, usually serverside.
      let error: string | null = null;
      let promises = [
        new Promise((resolve, reject) => {
          // local validation logic here.
          if (values.phoneNumber && !utils.isValidPhone(values.phoneNumber)) {
            error = 'Please provide a valid phone number like: "123 456 7890"';
          }
          if (error != null) {
            reject(error);
          } else {
            resolve(null);
          }
        }),
        new Promise((resolve, reject) => {
          // do server validation logic here.
          resolve(null);
        }),
      ];
      let result = await Promise.all(promises);
      return result;
    },
    email: async function (values: RSASpecialistMeeingFormValues) {
      // Do validation here
      // two promises, one for local, one for async, usually serverside.
      let error: string | null = null;
      let promises = [
        new Promise((resolve, reject) => {
          // local validation logic here.
          if (!(values.email && isValidEmail(values.email) === true)) {
            error = "Please provide a valid email";
          }
          if (error != null) {
            reject(error);
          } else {
            resolve(null);
          }
        }),
        new Promise((resolve, reject) => {
          // do server validation logic here.
          resolve(null);
        }),
      ];
      let result = await Promise.all(promises);
      return result;
    },
    confirmEmail: async function (values: RSASpecialistMeeingFormValues) {
      // Do validation here
      // two promises, one for local, one for async, usually serverside.
      let error: string | null = null;
      let promises = [
        new Promise((resolve, reject) => {
          // local validation logic here.
          if (
            !(values.confirmEmail && isValidEmail(values.confirmEmail) === true)
          ) {
            error = "Please provide a valid email";
          } else if (values.confirmEmail !== values.email) {
            error = "Email entries does not match";
          }

          if (error != null) {
            reject(error);
          } else {
            resolve(null);
          }
        }),
        new Promise((resolve, reject) => {
          // do server validation logic here.
          resolve(null);
        }),
      ];
      let result = await Promise.all(promises);
      return result;
    },
    coordinatorId: async function (values: RSASpecialistMeeingFormValues) {
        // Do validation here
        // two promises, one for local, one for async, usually serverside.
        let error: string | null = null;
        let promises = [
          new Promise((resolve, reject) => {
            // local validation logic here.
            if (!(!!values.coordinatorId && values.coordinatorId > 0)) {
              error = "Please select an Employment Coordinator";
            }
  
            if (error != null) {
              reject(error);
            } else {
              resolve(null);
            }
          }),
          new Promise((resolve, reject) => {
            // do server validation logic here.
            resolve(null);
          }),
        ];
        let result = await Promise.all(promises);
        return result;
      },
    helpTopicIds: async function (values: RSASpecialistMeeingFormValues) {
      // Do validation here
      // two promises, one for local, one for async, usually serverside.
      let error: string | null = null;
      let promises = [
        new Promise((resolve, reject) => {
          // local validation logic here.
          if (!(values.helpTopicIds && values.helpTopicIds.length > 0)) {
            error = "Please select at least one Help Topic";
          }
          if (error != null) {
            reject(error);
          } else {
            resolve(null);
          }
        }),
        new Promise((resolve, reject) => {
          // do server validation logic here.
          resolve(null);
        }),
      ];
      let result = await Promise.all(promises);
      return result;
    },
    otherAssistance: async function (values: RSASpecialistMeeingFormValues) {
      // Do validation here
      // two promises, one for local, one for async, usually serverside.
      let error: string | null = null;
      let promises = [
        new Promise((resolve, reject) => {
          // local validation logic here.
          if (
            getShouldDisplayOtherField(values.helpTopicIds, masterData) ===
              true &&
            !values.otherAssistance
          ) {
            error = "Please enter other assistance requested";
          }
          if (error != null) {
            reject(error);
          } else {
            resolve(null);
          }
        }),
        new Promise((resolve, reject) => {
          // do server validation logic here.
          resolve(null);
        }),
      ];
      let result = await Promise.all(promises);
      return result;
    },
    preferedDateTime: async function (values: RSASpecialistMeeingFormValues) {
      // Do validation here
      // two promises, one for local, one for async, usually serverside.
      let error: string | null = null;
      let promises = [
        new Promise((resolve, reject) => {
          // local validation logic here.
          if (!values.preferedDateTime) {
            error = "Please enter Preferred Date Time";
          }
          if (error != null) {
            reject(error);
          } else {
            resolve(null);
          }
        }),
        new Promise((resolve, reject) => {
          // do server validation logic here.
          resolve(null);
        }),
      ];
      let result = await Promise.all(promises);
      return result;
    },
    comments: async function (values: RSASpecialistMeeingFormValues) {
      // Do validation here
      // two promises, one for local, one for async, usually serverside.
      let error: string | null = null;
      let promises = [
        new Promise((resolve, reject) => {
          // local validation logic here.
          if (!values.comments) {
            error = "Please enter comments";
          }
          if (error != null) {
            reject(error);
          } else {
            resolve(null);
          }
        }),
        new Promise((resolve, reject) => {
          // do server validation logic here.
          resolve(null);
        }),
      ];
      let result = await Promise.all(promises);
      return result;
    },
  };

  let isSubmit = false;
  const debounceTime = 250;
  const ref = useRef(initRefs(defaultValues));
  const [values, setValues] =
    useState<RSASpecialistMeeingFormValues>(defaultValues);
  const [errors, setErrors] = useState<RSASpecialistMeeingFormErrors>({});
  const [touched, setTouched] = useState<RSASpecialistMeeingFormTouched>({});
  //const debouncedValidateForm = _.debounce(validateForm, debounceTime);
  const debouncedValidateForm = validateForm;
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [masterData, setMasterData] =
    useState<RSASpecialistMeeingFormMasterData>([]);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [coordinatorMaster, setCoordinatorMaster] = useState<DropdownOption[]>([]);

  let validationCount = 0;
  let validationTotal = 1;

  function initRefs(viewModel: RSASpecialistMeeingFormValues) {
    if (viewModel == null) {
      return {};
    } else {
      let result: RSASpecialistMeeingFormRef = {};
      Object.keys(viewModel).forEach((key) => {
        result[key] = React.createRef<HTMLElement | null>();
      });
      return result;
    }
  }

  function registerRef(ref: HTMLElement | null) {
    console.log(ref);
    console.log(typeof ref);
  }

  function touchAll() {
    let keys = Object.keys(defaultValues);
    keys.forEach((key) => {
      touched[key] = true;
    });
    setTouched(touched);
  }

  function counterHit(currentError: RSASpecialistMeeingFormErrors) {
    validationCount += 1;
    // we did not handle timeout problem, but it will in future
    if (validationCount === validationTotal) {
      validationCount = 0;
      if (_.isEmpty(currentError)) {
        setIsValid(true);
        if (isSubmit === true) {
          isSubmit = false;
          setIsLoading(true);
          saveData({ ...values });
        } else {
          setIsLoading(false);
        }
      } else {
        if (isSubmit === true) {
          isSubmit = false;
        }
        if (isValid !== false) {
          setIsValid(false);
        }
        setIsLoading(false);
      }
    }
  }

  function saveData(values: RSASpecialistMeeingFormValues) {
    setIsSubmitting(true);
    setIsLoading(true);
    setErrorMessage(null);
    let newData = _.cloneDeep(values);
    newData.PreferredTimes = newData.preferedDateTime;
    delete newData.preferedDateTime;
    newData.RequestorComments = newData.comments;
    delete newData.comments;
    newData.HelpTopics = newData.helpTopicIds.map((topicId: any) => {
      return {
        EmploymentHelpTopicId: topicId,
        OtherDescription: masterData.filter((item) => {
          return item.employmentHelpTopicId === topicId;
        })[0].askMoreInfo
          ? newData.otherAssistance
          : null,
      };
    });
    delete newData.helpTopicIds;
    delete newData.confirmEmail;
    delete newData.otherAssistance;
    newData.phoneNumber = utils.formatPhoneNumber(newData.phoneNumber);
    postMeetingRequest(newData, false).then(
      (res) => {
        setIsLoading(false);
        setIsSubmitting(false);
        if (res && res.status === 200) {
          setSubmitSuccess(true);
          //console.log(res);
        } else if (res && res.status === 401) {
          setIsValid(false);
        } else {
          Swal.fire({
            icon: "error",
            title: "Error While Processing",

            text: `${
              res && res.data
                ? // @ts-expect-error
                  res.data.message
                  ? // @ts-expect-error
                    res.data.message
                  : ""
                : "No detail reason available."
            }`,
            showConfirmButton: true,
            confirmButtonText: "OK",
            confirmButtonColor: "#1678c2",
          }).then(() => {});
        }
      },
      (error) => {
        setIsLoading(false);
        setIsSubmitting(false);
        if (
          error.response &&
          error.response.data &&
          error.response.status === 500
        ) {
          let footer = "";
          Swal.fire({
            icon: "error",
            title: "Error While Processing",
            text: `${
              error.response.data.message
                ? error.response.data.message
                : "No detail reason available"
            }`,
            showConfirmButton: true,
            footer: footer,
            confirmButtonText: "OK",
          }).then(() => {});
        }
      }
    );
  }

  function resetForm() {
    Object.keys(defaultValues).forEach((key, index) => {
      let temp = ref.current[key].current;
      if (temp != null) {
        // not implemented yet
      }
    });
    setValues(defaultValues);
    setIsLoading(false);
    setTouched({});
    setErrors({});
  }

  async function handleSubmit() {
    touchAll();
    setIsLoading(true);
    validateForm(values, validations, null);
    isSubmit = true;
  }

  function validateForm(
    values: RSASpecialistMeeingFormValues,
    validations: ValidtionFunctions,
    fieldName: string | null = null
  ) {
    let newErrors: RSASpecialistMeeingFormErrors = {};
    setIsLoading(true);
    if (fieldName != null) {
      // only validate the changing field.Keep current errors info
      newErrors = errors;
      validationTotal = 1;
      validationCount = 0;
      validations[fieldName](values).then(
        () => {
          delete newErrors[fieldName];
          setErrors(newErrors);
          counterHit(newErrors);
        },
        (error: string) => {
          newErrors[fieldName] = error;
          setErrors(newErrors);
          counterHit(newErrors);
        }
      );
    } else {
      // if this validate is not trigered by input control change, validate all.
      let keys = Object.keys(defaultValues);
      validationTotal = keys.length > 0 ? keys.length : 0;
      validationCount = 0;
      keys.forEach((key) => {
        validations[key](values).then(
          () => {
            delete newErrors[key];
            setErrors(newErrors);
            counterHit(newErrors);
          },
          (error: string) => {
            newErrors[key] = error;
            setErrors(newErrors);
            counterHit(newErrors);
          }
        );
      });
    }
  }

  function handleBlur(fieldName: string) {
    if (errorMessage != null) {
      setErrorMessage(null);
    }
    setTimeout(() => {
      setTouched((prevValue) => {
        return {
          ...prevValue,
          [fieldName]: true,
        };
      });
      validateForm(values, validations, null);
    }, 250);
  }

  function handleChange(newValue: any, fieldName: string, fieldOnly = true) {
    if (errorMessage != null) {
      setErrorMessage(null);
    }
    if (fieldName === "phoneNumber") {
      if (newValue && utils.isValidPhone(newValue)) {
        newValue = utils.formatPhoneNumber(newValue);
        newValue = utils.formatPhoneNumber(newValue);
      }
    }
    values[fieldName] = newValue;
    setValues(values);
    if (!(touched[fieldName] != null)) {
      setTouched({ ...touched, [fieldName]: true });
    }
    debouncedValidateForm(values, validations, fieldOnly ? fieldName : null);
  }

  const getShouldDisplayOtherField = (
    currValues: number[],
    masterData: RSASpecialistMeeingFormMasterData
  ): boolean => {
    let result = false;
    masterData.forEach((item) => {
      if (item.askMoreInfo) {
        if (currValues.includes(item.employmentHelpTopicId)) {
          result = true;
        }
      }
    });
    return result;
  };

  const shouldDisplayOtherField: boolean = getShouldDisplayOtherField(
    values.helpTopicIds,
    masterData
  );
  const otherFieldText: string | null = shouldDisplayOtherField
    ? masterData.filter((item) => {
        return item.askMoreInfo === true;
      })[0].moreInfoText
    : "";

  function isValidEmail(testTarget: string) {
    let myReg = /\b[\w\.-]+@[\w\.-]+\.\w{2,4}\b/gi;
    return myReg.test(testTarget);
  }

  return [
    values,
    masterData,
    coordinatorMaster,
    errors,
    touched,
    isLoading,
    isSubmitting,
    isValid,
    submitSuccess,
    errorMessage,
    shouldDisplayOtherField,
    otherFieldText,
    registerRef,
    handleChange,
    handleBlur,
    handleSubmit,
  ] as const;
}
